import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, ListChecks, Loader2 } from "lucide-react";
import { SetupDashboard } from "@/components/SetupDashboard";

export default function AssignerSetupDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [zone, setZone] = useState("all");

  useEffect(() => {
    if (user?.account_kind === "restricted") { navigate("/atayici", { replace: true }); return; }
    api.get("/assigner/projects").then((r) => { setRows(r.data || []); setLoaded(true); }).catch((e) => { toast.error(apiError(e)); setLoaded(true); });
  }, [user, navigate]);

  const zones = useMemo(() => {
    const zs = [...new Set(rows.map((p) => p.zone).filter(Boolean))].sort();
    return [["all", "Tümü"], ...zs.map((z) => [z, z])];
  }, [rows]);

  const filtered = useMemo(() => rows.filter((p) => zone === "all" || p.zone === zone), [rows, zone]);

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="assigner-setup-dashboard-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-heading text-xl font-extrabold tracking-tight">Gett<span className="text-slate-400">Proposal</span></div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold flex items-center gap-1"><ListChecks size={10} /> Setup Dashboard</div>
          </div>
          <button onClick={() => navigate("/atayici")} data-testid="setup-dash-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-900 px-3 py-2 transition-colors duration-150">
            <ArrowLeft size={14} /> Geri Dön
          </button>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Setup Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">{loaded ? `${filtered.length} proje — kurulum takip görünümü` : "Yükleniyor..."}</p>
        {!loaded && <div className="flex justify-center py-16 text-slate-400"><Loader2 size={22} className="animate-spin" /></div>}
        {loaded && (
          <>
            {zones.length > 2 && (
              <div className="mt-5 mb-6 flex border border-slate-200 w-fit bg-white">
                {zones.map(([k, lbl]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setZone(k)}
                    data-testid={`assigner-setup-zone-${k === "all" ? "all" : k.split(" ")[0].toLowerCase()}`}
                    className={`px-4 py-2 text-xs font-bold border-b-2 transition-colors duration-150 ${zone === k ? (k === "Green Zone" ? "text-emerald-600 border-emerald-600" : k === "Blue Zone" ? "text-blue-600 border-blue-600" : "text-slate-900 border-slate-900") : "border-transparent text-slate-400 hover:text-slate-700"}`}
                  >
                    {lbl} ({rows.filter((p) => k === "all" || p.zone === k).length})
                  </button>
                ))}
              </div>
            )}
            <div className={zones.length > 2 ? "" : "mt-6"}>
              <SetupDashboard rows={filtered} testidPrefix="assigner-setup" />
            </div>
          </>
        )}
      </main>
    </div>
  );
}
